<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { isSendableTemplate } from '@chatwoot/utils';
import InboxesAPI from 'dashboard/api/inboxes';
import InteractiveTemplatesAPI from 'dashboard/api/whatsappInteractiveMessageTemplates';
import Select from 'dashboard/components-next/select/Select.vue';
import { useMapGetter } from 'dashboard/composables/store';
import { insertAtCursor } from 'dashboard/helper/systemParameters';
import SystemParameterMenu from 'dashboard/components-next/systemParameters/SystemParameterMenu.vue';

// Mensagem de envio do modelo (metadata.delivery_message), usada por
// Talkvia::Contracts::DeliveryMessage ao enviar o orçamento ou o link do contrato.
const props = defineProps({
  modelValue: { type: Object, default: null },
  kind: { type: String, default: 'quote' },
});
const emit = defineEmits(['update:modelValue']);

const TYPES = [
  {
    value: 'text',
    label: 'Mensagem simples',
    hint: 'Texto do modelo, como hoje.',
    icon: 'i-hugeicons-message-01',
  },
  {
    value: 'meta_template',
    label: 'Template Meta',
    hint: 'Template aprovado; funciona fora das 24 h.',
    icon: 'i-hugeicons-whatsapp',
  },
  {
    value: 'interactive',
    label: 'Mensagem interativa',
    hint: 'Com botões, dentro da janela de 24 h.',
    icon: 'i-hugeicons-cursor-pointer-02',
  },
];
const FIELDS = {
  quote: [
    ['nome', 'Nome'],
    ['numero_orcamento', 'Nº do orçamento'],
    ['total', 'Total'],
    ['validade', 'Validade'],
    ['data_entrega', 'Entrega'],
    ['data_retirada', 'Retirada'],
    ['forma_pagamento', 'Pagamento'],
    ['produtos', 'Produtos'],
    ['endereco_completo', 'Endereço'],
    ['link_assinatura', 'Link de assinatura'],
  ],
  contract: [
    ['nome', 'Nome'],
    ['empresa', 'Empresa'],
    ['link', 'Link do contrato'],
  ],
};
const linkField = computed(() =>
  props.kind === 'quote' ? 'link_assinatura' : 'link'
);
const fields = computed(() => FIELDS[props.kind] || FIELDS.quote);

const initial = props.modelValue || {};
const type = ref(initial.type || 'text');
const template = reactive({
  inbox_id: '',
  name: '',
  language: '',
  category: '',
  parameter_format: '',
  body: '',
  variables: {},
  ...(initial.template || {}),
});
const interactive = reactive({
  saved_template_id: '',
  body: '',
  header_text: '',
  footer_text: '',
  action: props.kind === 'contract' ? 'link' : 'buttons',
  buttons: ['Aceitar orçamento', 'Tenho dúvidas', ''],
  cta_label: props.kind === 'contract' ? 'Assinar contrato' : 'Ver orçamento',
  cta_url: `{{${props.kind === 'quote' ? 'link_assinatura' : 'link'}}}`,
  ...(initial.interactive || {}),
});
if (initial.interactive && !initial.interactive.action)
  interactive.action = initial.interactive.cta_url ? 'link' : 'buttons';
while (interactive.buttons.length < 3) interactive.buttons.push('');

const value = computed(() => {
  if (type.value === 'meta_template')
    return { type: 'meta_template', template: { ...template } };
  if (type.value === 'interactive' && interactive.saved_template_id)
    return {
      type: 'interactive',
      interactive: { saved_template_id: interactive.saved_template_id },
    };
  if (type.value === 'interactive') {
    const link = interactive.action === 'link';
    return {
      type: 'interactive',
      interactive: {
        body: interactive.body,
        header_text: interactive.header_text,
        footer_text: interactive.footer_text,
        action: interactive.action,
        buttons: link ? [] : interactive.buttons.filter(Boolean),
        cta_label: link ? interactive.cta_label : '',
        cta_url: link ? interactive.cta_url : '',
      },
    };
  }
  return { type: 'text' };
});
watch(value, next => emit('update:modelValue', next), {
  deep: true,
  immediate: true,
});

// Mensagens interativas já criadas (Configurações → Mensagens interativas).
const savedInteractives = ref([]);
const loadSavedInteractives = async () => {
  if (savedInteractives.value.length) return;
  try {
    const { data } = await InteractiveTemplatesAPI.get();
    savedInteractives.value = Array.isArray(data) ? data : data?.payload || [];
  } catch {
    savedInteractives.value = [];
  }
};
watch(type, current => current === 'interactive' && loadSavedInteractives(), {
  immediate: true,
});
const INTERACTIVE_TYPE_LABELS = {
  button: 'Botões de resposta',
  cta_url: 'Botão com link',
  voice_call: 'Ligar no WhatsApp',
  flow: 'WhatsApp Flow',
  location_request_message: 'Pedir localização',
  catalog_message: 'Catálogo',
  order_details: 'Pagamento Pix',
};
const savedOptions = computed(() => [
  { value: '', label: 'Criar a mensagem aqui' },
  ...savedInteractives.value.map(item => ({
    value: item.id,
    label: `${item.name} · ${INTERACTIVE_TYPE_LABELS[item.interactive?.type] || 'Interativa'}`,
  })),
]);
const savedInteractive = computed(() =>
  savedInteractives.value.find(
    item => String(item.id) === String(interactive.saved_template_id)
  )
);

// Templates aprovados da caixa escolhida.
const inboxes = useMapGetter('inboxes/getInboxes');
const whatsappInboxes = computed(() =>
  (inboxes.value || []).filter(
    inbox => inbox.channel_type === 'Channel::Whatsapp'
  )
);
const templates = ref([]);
const loadingTemplates = ref(false);
const templatesError = ref('');
const loadTemplates = async () => {
  templates.value = [];
  templatesError.value = '';
  if (!template.inbox_id) return;
  loadingTemplates.value = true;
  try {
    const { data } = await InboxesAPI.getMessageTemplates(template.inbox_id);
    const list = Array.isArray(data) ? data : data?.payload || [];
    templates.value = list.filter(isSendableTemplate);
  } catch {
    templatesError.value =
      'Não foi possível carregar os templates desta caixa.';
  } finally {
    loadingTemplates.value = false;
  }
};
watch(
  () => [type.value, template.inbox_id],
  () => type.value === 'meta_template' && loadTemplates(),
  { immediate: true }
);
const templateKey = item => `${item.name}::${item.language}`;
const bodyText = item =>
  (item?.components || []).find(component => component.type === 'BODY')?.text ||
  '';
const selectedKey = computed({
  get: () => (template.name ? `${template.name}::${template.language}` : ''),
  set: key => {
    const item = templates.value.find(entry => templateKey(entry) === key);
    const body = bodyText(item);
    const names = [
      ...new Set(
        [...body.matchAll(/\{\{\s*([^}]+?)\s*\}\}/g)].map(match =>
          match[1].trim()
        )
      ),
    ];
    Object.assign(template, {
      name: item?.name || '',
      language: item?.language || '',
      category: item?.category || '',
      parameter_format: item?.parameter_format || '',
      body,
      variables: Object.fromEntries(
        names.map(name => [name, template.variables[name] || ''])
      ),
    });
  },
});
const variableNames = computed(() => Object.keys(template.variables));

const fieldsRef = ref(null);
const insertInto = (target, key, token, selector) => {
  const element = fieldsRef.value?.querySelector(selector);
  target[key] = insertAtCursor(target[key], token, element).value;
};
const fieldToken = name => `{{${name}}}`;
const fieldHint = computed(() =>
  fields.value
    .slice(0, 3)
    .map(([name]) => fieldToken(name))
    .join(', ')
);
const INPUT =
  'w-full rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand';
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <section
    ref="fieldsRef"
    class="space-y-3 rounded-xl border border-n-weak bg-n-solid-2 p-4"
    aria-label="Mensagem de envio"
  >
    <div>
      <h4 class="m-0 text-sm font-semibold text-n-slate-12">
        Mensagem de envio
      </h4>
      <p class="mb-0 mt-1 text-xs text-n-slate-11">
        {{
          kind === 'quote'
            ? 'Como o orçamento chega ao cliente no WhatsApp. O PDF/PNG, quando escolhido, vai em mensagem separada.'
            : 'Como o link de assinatura chega ao cliente no WhatsApp.'
        }}
      </p>
    </div>
    <div class="grid gap-2 sm:grid-cols-3" role="radiogroup">
      <button
        v-for="option in TYPES"
        :key="option.value"
        type="button"
        role="radio"
        :aria-checked="type === option.value"
        class="flex items-start gap-2 rounded-lg border p-3 text-left transition"
        :class="
          type === option.value
            ? 'border-n-brand bg-n-brand/5'
            : 'border-n-weak hover:bg-n-alpha-1'
        "
        @click="type = option.value"
      >
        <span
          :class="option.icon"
          class="mt-0.5 size-4 shrink-0 text-n-brand"
        />
        <span class="min-w-0">
          <span class="block text-sm font-medium text-n-slate-12">
            {{ option.label }}
          </span>
          <span class="block text-xs text-n-slate-11">{{ option.hint }}</span>
        </span>
      </button>
    </div>

    <template v-if="type === 'meta_template'">
      <label class="grid gap-1.5 text-sm font-medium text-n-slate-12">
        Caixa do WhatsApp
        <Select
          v-model="template.inbox_id"
          :options="
            whatsappInboxes.map(inbox => ({
              value: inbox.id,
              label: inbox.name,
            }))
          "
          placeholder="Escolha a caixa"
          aria-label="Caixa do WhatsApp"
          class="!w-full [&>select]:w-full"
        />
      </label>
      <label
        v-if="template.inbox_id"
        class="grid gap-1.5 text-sm font-medium text-n-slate-12"
      >
        Template aprovado
        <Select
          v-model="selectedKey"
          :options="
            templates.map(item => ({
              value: templateKey(item),
              label: `${item.name} · ${item.language}`,
            }))
          "
          :placeholder="
            loadingTemplates ? 'Carregando...' : 'Escolha o template'
          "
          :disabled="loadingTemplates"
          aria-label="Template aprovado"
          class="!w-full [&>select]:w-full"
        />
      </label>
      <p v-if="templatesError" role="alert" class="m-0 text-xs text-n-ruby-11">
        {{ templatesError }}
      </p>
      <p
        v-if="template.body"
        class="m-0 whitespace-pre-wrap rounded-lg bg-n-alpha-1 p-3 text-sm text-n-slate-12"
      >
        {{ template.body }}
      </p>
      <div
        v-for="name in variableNames"
        :key="name"
        class="grid gap-1.5 text-sm"
      >
        <span class="flex items-center justify-between gap-2">
          <span class="font-medium text-n-slate-12">{{
            fieldToken(name)
          }}</span>
          <SystemParameterMenu
            @insert="
              token =>
                insertInto(
                  template.variables,
                  name,
                  token,
                  `[data-variable='${name}']`
                )
            "
          />
        </span>
        <input
          v-model="template.variables[name]"
          :data-variable="name"
          :class="INPUT"
          placeholder="Texto ou campo, ex.: {{nome}}"
        />
        <span class="flex flex-wrap gap-1">
          <button
            v-for="[field, label] in fields"
            :key="field"
            type="button"
            class="rounded-md bg-n-alpha-2 px-2 py-0.5 text-xs text-n-slate-11 hover:text-n-slate-12"
            @click="
              insertInto(
                template.variables,
                name,
                fieldToken(field),
                `[data-variable='${name}']`
              )
            "
          >
            {{ label }}
          </button>
        </span>
      </div>
    </template>

    <template v-if="type === 'interactive'">
      <label class="grid gap-1.5 text-sm font-medium text-n-slate-12">
        Mensagem interativa
        <Select
          v-model="interactive.saved_template_id"
          :options="savedOptions"
          aria-label="Mensagem interativa"
          class="!w-full [&>select]:w-full"
        />
      </label>
      <div
        v-if="interactive.saved_template_id"
        class="space-y-1 rounded-lg border border-n-weak bg-n-alpha-1 p-3 text-sm"
      >
        <p class="m-0 whitespace-pre-wrap text-n-slate-12">
          {{ savedInteractive?.content || 'Carregando...' }}
        </p>
        <p class="m-0 text-xs text-n-slate-11">
          Os campos do documento ({{ fieldHint }}) e os parâmetros do sistema
          escritos no texto, cabeçalho, rodapé ou link são preenchidos no envio.
        </p>
      </div>
    </template>
    <template v-if="type === 'interactive' && !interactive.saved_template_id">
      <label class="grid gap-1.5 text-sm font-medium text-n-slate-12">
        Cabeçalho (opcional)
        <input
          v-model="interactive.header_text"
          data-field="header"
          maxlength="60"
          :class="INPUT"
        />
      </label>
      <div class="grid gap-1.5 text-sm">
        <span class="flex items-center justify-between gap-2">
          <span class="font-medium text-n-slate-12">Mensagem</span>
          <SystemParameterMenu
            @insert="
              token =>
                insertInto(interactive, 'body', token, `[data-field='body']`)
            "
          />
        </span>
        <textarea
          v-model="interactive.body"
          data-field="body"
          rows="4"
          maxlength="1024"
          :class="INPUT"
        />
        <span class="flex flex-wrap gap-1">
          <button
            v-for="[field, label] in fields"
            :key="field"
            type="button"
            class="rounded-md bg-n-alpha-2 px-2 py-0.5 text-xs text-n-slate-11 hover:text-n-slate-12"
            @click="
              insertInto(
                interactive,
                'body',
                fieldToken(field),
                `[data-field='body']`
              )
            "
          >
            {{ label }}
          </button>
        </span>
      </div>
      <label class="grid gap-1.5 text-sm font-medium text-n-slate-12">
        Rodapé (opcional)
        <input
          v-model="interactive.footer_text"
          maxlength="60"
          :class="INPUT"
        />
      </label>
      <div class="flex flex-wrap gap-2 text-sm">
        <label class="inline-flex items-center gap-1.5">
          <input v-model="interactive.action" type="radio" value="buttons" />
          Botões de resposta
        </label>
        <label class="inline-flex items-center gap-1.5">
          <input v-model="interactive.action" type="radio" value="link" />
          Botão com link
        </label>
      </div>
      <div
        v-if="interactive.action === 'buttons'"
        class="grid gap-2 sm:grid-cols-3"
      >
        <input
          v-for="(_, index) in interactive.buttons"
          :key="index"
          v-model="interactive.buttons[index]"
          maxlength="20"
          :class="INPUT"
          :placeholder="`Botão ${index + 1}`"
        />
      </div>
      <div v-else class="grid gap-2 sm:grid-cols-2">
        <label class="grid gap-1.5 text-sm font-medium text-n-slate-12">
          Texto do botão
          <input
            v-model="interactive.cta_label"
            maxlength="20"
            :class="INPUT"
          />
        </label>
        <label class="grid gap-1.5 text-sm font-medium text-n-slate-12">
          Link
          <input
            v-model="interactive.cta_url"
            :class="INPUT"
            :placeholder="`{{${linkField}}} ou https://...`"
          />
        </label>
      </div>
    </template>
  </section>
</template>
